import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  SearchIcon,
  FilterIcon,
  DownloadIcon,
  ChevronRightIcon,
} from "./SettlementIcons";
import { DATE_RANGE_PRESETS, formatDateRangeSummary } from "../../utils/dateRangeUtils";
import useDebounce from "../../utils/useDebounce";
import "./filter-toolbar.scss"

/* =========================================================================
 * FilterToolbar
 * -------------------------------------------------------------------------
 * A fully generic, schema-driven filter bar that can be bound to ANY table.
 * You describe *what* filters exist via the `filters` prop; this component
 * only knows *how* to render + operate each filter `type`. Add as many
 * filters as you want, repeat a type as many times as you want (e.g. two
 * "select" filters for two different columns), each keyed uniquely.
 *
 * Supported filter types:
 *   "sortBy"         — single-select list, e.g. sort_by=net_revenue
 *   "sortDirection"  — asc/desc segmented toggle
 *   "dateRange"      — preset pills (today/this_week/this_month/this_year/custom)
 *   "select"         — generic id-wise selector, single or multiple, searchable
 *   "status"         — multi-select colored-dot status filter
 *
 * This component is fully controlled: every filter entry owns its own
 * `value` + `onChange`, so it can be wired directly into any parent state
 * (useState, useReducer, react-query params, URL search params, etc).
 *
 * Usage:
 *   <FilterToolbar
 *     searchValue={search}
 *     onSearchChange={setSearch}
 *     searchPlaceholder="Search restaurant..."
 *     showExport
 *     onExport={handleExport}
 *     filters={[
 *       { key: "sortBy", type: "sortBy", label: "Sort By", value: sortBy,
 *         onChange: setSortBy, defaultValue: "name",
 *         options: [
 *           { value: "name", label: "Restaurant Name" },
 *           { value: "member_spend", label: "Member Spend" },
 *           { value: "net_revenue", label: "Net Restaurant Revenue" },
 *         ] },
 *       { key: "sortDir", type: "sortDirection", label: "Sort Direction",
 *         value: sortDir, onChange: setSortDir, defaultValue: "desc" },
 *       { key: "period", type: "dateRange", label: "Settlement Period",
 *         value: period, onChange: setPeriod, defaultValue: { preset: "this_week" } },
 *       { key: "restaurant", type: "select", label: "Restaurant", multiple: true,
 *         searchable: true, value: restaurantIds, onChange: setRestaurantIds,
 *         options: restaurants.map(r => ({ value: r.id, label: r.name })) },
 *       { key: "status", type: "status", label: "Status", value: statuses,
 *         onChange: setStatuses,
 *         options: [
 *           { value: "paid", label: "Paid", tone: "green" },
 *           { value: "pending", label: "Pending", tone: "orange" },
 *           { value: "failed", label: "Failed", tone: "red" },
 *         ] },
 *     ]}
 *   />
 * ======================================================================= */

const isActiveValue = (filter) => {
  const { type, value, defaultValue } = filter;
  if (type === "dateRange") {
    const preset = value?.preset;
    const defPreset = defaultValue?.preset || "all";
    // Check if preset is different from default OR if custom dates are set
    if (Boolean(preset) && preset !== defPreset) return true;
    // For custom date range, check if start or end dates are set
    if (preset === "custom" && (value?.start || value?.end)) return true;
    return false;
  }
  if (type === "select" || type === "status") {
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  }
  if (type === "sortBy") {
    return Boolean(value) && value !== defaultValue;
  }
  if (type === "sortDirection") {
    return Boolean(value) && value !== (defaultValue || "asc");
  }
  return Boolean(value);
};

const summarizeFilter = (filter) => {
  const { type, value, label, options = [] } = filter;
  if (type === "dateRange") return `${label}: ${formatDateRangeSummary(value)}`;
  if (type === "sortBy") {
    const opt = options.find((o) => o.value === value);
    return `${label}: ${opt ? opt.label : value}`;
  }
  if (type === "sortDirection") return `${label}: ${value === "asc" ? "Ascending" : "Descending"}`;
  if (type === "select" || type === "status") {
    const values = Array.isArray(value) ? value : [value];
    if (values.length === 1) {
      const opt = options.find((o) => o.value === values[0]);
      return `${label}: ${opt ? opt.label : values[0]}`;
    }
    return `${label}: ${values.length} selected`;
  }
  return `${label}: ${value}`;
};

const clearFilterValue = (filter) => {
  const { type, defaultValue } = filter;
  if (type === "select" || type === "status") return [];
  if (type === "dateRange") return defaultValue || { preset: "all", start: "", end: "" };
  return defaultValue ?? "";
};

/* ---------------------------- Section renderers ---------------------------- */

const SortBySection = ({ filter }) => (
  <div className="ft-section">
    <p className="ft-section__title">{filter.label}</p>
    <div className="ft-radio-list">
      {(filter.options || []).map((opt) => (
        <button
          type="button"
          key={opt.value}
          className={`ft-radio-item ${filter.value === opt.value ? "ft-radio-item--active" : ""}`}
          onClick={() => filter.onChange(opt.value)}
        >
          <span className="ft-radio-item__dot" />
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const SortDirectionSection = ({ filter }) => (
  <div className="ft-section">
    <p className="ft-section__title">{filter.label}</p>
    <div className="ft-segmented">
      <button
        type="button"
        className={`ft-segmented__btn ${filter.value === "asc" ? "ft-segmented__btn--active" : ""}`}
        onClick={() => filter.onChange("asc")}
      >
        ↑ Ascending
      </button>
      <button
        type="button"
        className={`ft-segmented__btn ${filter.value === "desc" ? "ft-segmented__btn--active" : ""}`}
        onClick={() => filter.onChange("desc")}
      >
        ↓ Descending
      </button>
    </div>
  </div>
);

const DateRangeSection = ({ filter }) => {
  const value = filter.value || {};
  const setPreset = (preset) => filter.onChange({ ...value, preset });
  const hidePresets = filter.hidePresets || false;
  return (
    <div className="ft-section">
      <p className="ft-section__title">{filter.label}</p>
      {!hidePresets && (
        <div className="ft-pill-row">
          {DATE_RANGE_PRESETS.map((p) => (
            <button
              type="button"
              key={p.value}
              className={`ft-pill ${value.preset === p.value ? "ft-pill--active" : ""}`}
              onClick={() => setPreset(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
      {hidePresets || value.preset === "custom" ? (
        <div className="ft-date-inputs">
          <label className="ft-date-inputs__field">
            <span>From</span>
            <input
              type="date"
              value={value.start || ""}
              onChange={(e) => filter.onChange({ ...value, start: e.target.value })}
            />
          </label>
          <label className="ft-date-inputs__field">
            <span>To</span>
            <input
              type="date"
              value={value.end || ""}
              onChange={(e) => filter.onChange({ ...value, end: e.target.value })}
            />
          </label>
        </div>
      ) : null}
    </div>
  );
};

const SelectSection = ({ filter }) => {
  const [query, setQuery] = useState("");
  const multiple = Boolean(filter.multiple);
  const selected = multiple ? (filter.value || []) : filter.value ? [filter.value] : [];

  const options = useMemo(() => {
    const opts = filter.options || [];
    if (!query.trim()) return opts;
    return opts.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()));
  }, [filter.options, query]);

  const toggle = (val) => {
    if (multiple) {
      const next = selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val];
      filter.onChange(next);
    } else {
      filter.onChange(selected.includes(val) ? "" : val);
    }
  };

  return (
    <div className="ft-section">
      <div className="ft-section__title-row">
        <p className="ft-section__title">{filter.label}</p>
        {multiple && selected.length > 0 && (
          <button type="button" className="ft-section__link" onClick={() => filter.onChange([])}>
            Clear
          </button>
        )}
      </div>
      {filter.searchable && (
        <div className="ft-select-search">
          <SearchIcon />
          <input
            type="text"
            placeholder={`Search ${filter.label.toLowerCase()}...`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      <div className="ft-checkbox-list">
        {options.length === 0 && <p className="ft-empty-note">No matches</p>}
        {options.map((opt) => (
          <label className="ft-checkbox-item" key={opt.value}>
            <input
              type={multiple ? "checkbox" : "radio"}
              checked={selected.includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

const StatusSection = ({ filter }) => {
  const selected = filter.value || [];
  const toggle = (val) => {
    const next = selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val];
    filter.onChange(next);
  };
  return (
    <div className="ft-section">
      <div className="ft-section__title-row">
        <p className="ft-section__title">{filter.label}</p>
        {selected.length > 0 && (
          <button type="button" className="ft-section__link" onClick={() => filter.onChange([])}>
            Clear
          </button>
        )}
      </div>
      <div className="ft-status-list">
        {(filter.options || []).map((opt) => (
          <label
            className={`ft-status-item ft-status-item--${opt.tone || "grey"} ${
              selected.includes(opt.value) ? "ft-status-item--active" : ""
            }`}
            key={opt.value}
          >
            <input
              type="checkbox"
              checked={selected.includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span className="ft-status-item__dot" />
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
};

const FILTER_SECTION_COMPONENTS = {
  sortBy: SortBySection,
  sortDirection: SortDirectionSection,
  dateRange: DateRangeSection,
  select: SelectSection,
  status: StatusSection,
};

/* ---------------------------------- Main ---------------------------------- */

const FilterToolbar = ({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  filters,
  showExport,
  onExport,
  className,
}) => {
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);
  const [localSearchValue, setLocalSearchValue] = useState(searchValue || "");

  const debouncedSearchChange = useDebounce((value) => {
    onSearchChange && onSearchChange(value);
  }, 300);

  // Sync local state with prop when prop changes externally
  useEffect(() => {
    setLocalSearchValue(searchValue || "");
  }, [searchValue]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const activeFilters = useMemo(() => filters.filter(isActiveValue), [filters]);
  const activeCount = activeFilters.length;

  const handleClearAll = () => {
    filters.forEach((f) => f.onChange(clearFilterValue(f)));
  };

  return (
    <div className={`ft-wrap ${className || ""}`}>
      <div className="ft-toolbar">
        <div className="ft-toolbar__search">
          <SearchIcon />
          <input
            type="text"
            value={localSearchValue}
            placeholder={searchPlaceholder || "Search..."}
            onChange={(e) => {
              setLocalSearchValue(e.target.value);
              debouncedSearchChange(e.target.value);
            }}
          />
        </div>

        <div className="ft-toolbar__actions">
          {filters.length > 0 && (
            <div className="ft-filters-anchor">
              <button
                type="button"
                ref={btnRef}
                className={`ft-filters-btn ${open ? "ft-filters-btn--open" : ""}`}
                onClick={() => setOpen((o) => !o)}
              >
                <FilterIcon />
                Filters
                {activeCount > 0 && <span className="ft-filters-btn__count">{activeCount}</span>}
              </button>

              {open && (
                <div className="ft-panel" ref={panelRef}>
                  <div className="ft-panel__header">
                    <span>Filters</span>
                    {activeCount > 0 && (
                      <button type="button" className="ft-panel__clear" onClick={handleClearAll}>
                        Clear all
                      </button>
                    )}
                  </div>
                  <div className="ft-panel__body">
                    {filters.map((f) => {
                      const Section = FILTER_SECTION_COMPONENTS[f.type];
                      if (!Section) return null;
                      return (
                        <React.Fragment key={f.key}>
                          <Section filter={f} />
                        </React.Fragment>
                      );
                    })}
                  </div>
                  <div className="ft-panel__footer">
                    <button type="button" className="ft-panel__done-btn" onClick={() => setOpen(false)}>
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {showExport && (
            <button type="button" className="ft-export-btn" onClick={onExport} title="Export">
              <DownloadIcon />
            </button>
          )}
        </div>
      </div>

      {activeCount > 0 && (
        <div className="ft-chips">
          {activeFilters.map((f) => (
            <span className="ft-chip" key={f.key}>
              {summarizeFilter(f)}
              <button
                type="button"
                onClick={() => f.onChange(clearFilterValue(f))}
                aria-label={`Remove ${f.label} filter`}
              >
                ×
              </button>
            </span>
          ))}
          <button type="button" className="ft-chips__clear" onClick={handleClearAll}>
            Clear all <ChevronRightIcon />
          </button>
        </div>
      )}
    </div>
  );
};

const filterShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["sortBy", "sortDirection", "dateRange", "select", "status"]).isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      tone: PropTypes.string,
    })
  ),
  multiple: PropTypes.bool,
  searchable: PropTypes.bool,
  defaultValue: PropTypes.any,
});

FilterToolbar.propTypes = {
  searchValue: PropTypes.string,
  onSearchChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  filters: PropTypes.arrayOf(filterShape),
  showExport: PropTypes.bool,
  onExport: PropTypes.func,
  className: PropTypes.string,
};

FilterToolbar.defaultProps = {
  searchValue: "",
  onSearchChange: null,
  searchPlaceholder: "Search...",
  filters: [],
  showExport: false,
  onExport: () => {},
  className: "",
};

export default FilterToolbar;
